import { useLazyQuery } from "@apollo/client/react";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { RunState } from "types/generated";
import { downloadFileFromURL } from "utils/file";

import { GET_STACK_CPU_PROFILE_DOWNLOAD_URL } from "./gql";
import { getCpuProfileFileName, getCpuProfileUnavailableMessage } from "./helpers";

type UseDownloadCpuProfileProps = {
  phase: RunState;
  runId: string;
  stackId: string;
  runState: RunState;
};

const useDownloadCpuProfile = ({ phase, runId, stackId, runState }: UseDownloadCpuProfileProps) => {
  const { reportError, reportInfo } = useTypedContext(FlashContext);
  const trackSegmentAnalyticsEvent = useAnalytics();

  // The pre-signed URL is short-lived, so never reuse a cached one
  const [getDownloadUrl, { loading }] = useLazyQuery(GET_STACK_CPU_PROFILE_DOWNLOAD_URL, {
    fetchPolicy: "network-only",
  });

  const downloadCpuProfile = useCallback(async () => {
    try {
      const { data, error } = await getDownloadUrl({
        variables: { stackId, runId, state: phase },
        context: { suppressFlashError: true },
      });

      if (error) {
        throw error;
      }

      const cpuProfileDownloadURL = data?.stack?.run?.cpuProfileDownloadURL;

      if (!cpuProfileDownloadURL?.url) {
        reportInfo({ message: getCpuProfileUnavailableMessage(cpuProfileDownloadURL) });
        return;
      }

      downloadFileFromURL(cpuProfileDownloadURL.url, getCpuProfileFileName(runId, phase));

      trackSegmentAnalyticsEvent("Run Logs - download CPU profile", {
        cpuProfileState: phase,
        runState,
      });
    } catch {
      reportError({
        title: "Download failed",
        message: "Failed to download the CPU profile. Please try again later.",
      });
    }
  }, [
    getDownloadUrl,
    stackId,
    runId,
    phase,
    reportError,
    reportInfo,
    trackSegmentAnalyticsEvent,
    runState,
  ]);

  return { downloadCpuProfile, loading };
};

export default useDownloadCpuProfile;
